import type { ReactNode } from 'react';

import { useCallback, useState } from 'react';
import { AlertCircle, AlertTriangle, Check, Copy, Info } from 'lucide-react';

import { PageContainer } from '@/components/layout/page-container';
import { cn } from '@/lib/utils';

/**
 * Reusable item-detail layout primitive — the shared shape behind every
 * "one record, expanded" screen (a delivery-log entry, a retry attempt, a
 * settings object). Composition mirrors Resend's own log-detail page:
 *   header block (icon tile + eyebrow/title) with a top-right action slot,
 *   a responsive metadata grid, an optional inline alert banner, and an
 *   optional code/JSON panel with its own copy button.
 *
 * Outer padding/max-width are NOT reinvented here — the whole thing composes
 * inside PageContainer (the single owner of screen spacing, per
 * page-container.tsx). Everything below the container is markup specific to
 * this primitive.
 */

type AlertTone = 'warning' | 'danger' | 'info';

export interface ItemDetailMetadata {
	label: string;
	value: ReactNode;
}

export interface ItemDetailAlert {
	tone: AlertTone;
	message: ReactNode;
	action?: ReactNode;
}

export interface ItemDetailCodePanel {
	heading: string;
	content: string;
}

export interface ItemDetailPageProps {
	/** Icon rendered centered inside the small colored header tile. */
	icon: ReactNode;
	/** Small muted label above the title (e.g. "Log"). */
	eyebrow: string;
	/** Bold primary title (e.g. "POST /emails"). */
	title: string;
	/** Top-right slot for action buttons / dropdowns. */
	actions?: ReactNode;
	/** Label/value pairs rendered in a responsive 2-or-3-column grid. */
	metadata: ItemDetailMetadata[];
	/** Optional inline colored banner. */
	alert?: ItemDetailAlert;
	/** Optional bordered code/JSON panel with a copy-to-clipboard button. */
	codePanel?: ItemDetailCodePanel;
	/** Extra body content rendered after the built-in sections. */
	children?: ReactNode;
}

const ALERT_TONE: Record<
	AlertTone,
	{ container: string; icon: typeof Info }
> = {
	warning: {
		container: 'border-warning/40 bg-warning/10 text-warning',
		icon: AlertTriangle,
	},
	danger: {
		container: 'border-danger/40 bg-danger/10 text-danger',
		icon: AlertCircle,
	},
	info: {
		container: 'border-info/40 bg-info/10 text-info',
		icon: Info,
	},
};

/** Copy-to-clipboard button with a brief "Copied" confirmation. */
function CopyButton({ value }: { value: string }) {
	const [copied, setCopied] = useState(false);

	const handleCopy = useCallback(() => {
		void navigator.clipboard.writeText(value).then(() => {
			setCopied(true);
			setTimeout(() => setCopied(false), 1500);
		});
	}, [value]);

	return (
		<button
			type="button"
			onClick={handleCopy}
			className="inline-flex items-center gap-1.5 border border-border-strong px-2 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
			aria-label={copied ? 'Copied' : 'Copy to clipboard'}
		>
			{copied ? (
				<Check className="size-3.5 text-success" aria-hidden="true" />
			) : (
				<Copy className="size-3.5" aria-hidden="true" />
			)}
			{copied ? 'Copied' : 'Copy'}
		</button>
	);
}

/**
 * Responsive 2-or-3-column label/value grid. Renders nothing when the list is
 * empty (same guard the inline copy carried), so callers can pass an unfiltered
 * array. Exported for reuse by any item-detail-style screen (delivery log,
 * webhooks, ...).
 */
export function MetadataGrid({ metadata }: { metadata: ItemDetailMetadata[] }) {
	if (metadata.length === 0) return null;

	return (
		<dl className="grid grid-cols-2 gap-x-6 gap-y-4 md:grid-cols-3">
			{metadata.map((item, i) => (
				<div key={`${item.label}-${i}`}>
					<dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
						{item.label}
					</dt>
					<dd className="mt-1 text-sm text-foreground">{item.value}</dd>
				</div>
			))}
		</dl>
	);
}

/**
 * Bordered code/JSON panel: heading + corner copy button over a mono `<pre>`.
 * Exported for reuse alongside MetadataGrid. Props are the ItemDetailCodePanel
 * shape so a caller can spread a panel object straight in.
 */
export function CodePanel({ heading, content }: ItemDetailCodePanel) {
	return (
		<section className="overflow-hidden rounded-lg border border-border">
			<div className="flex items-center justify-between border-b border-border bg-muted/50 px-4 py-2">
				<h2 className="text-sm font-medium text-foreground">{heading}</h2>
				<CopyButton value={content} />
			</div>
			<pre className="overflow-x-auto px-4 py-3 font-mono text-xs leading-relaxed text-foreground">
				{content}
			</pre>
		</section>
	);
}

export function ItemDetailPage({
	icon,
	eyebrow,
	title,
	actions,
	metadata,
	alert,
	codePanel,
	children,
}: ItemDetailPageProps) {
	const alertTone = alert ? ALERT_TONE[alert.tone] : null;
	const AlertIcon = alertTone?.icon;

	return (
		<PageContainer>
			{/* Header block: icon tile + eyebrow/title, with a top-right action row. */}
			<header className="flex items-start justify-between gap-4">
				<div className="flex items-center gap-4">
					<div className="flex size-10 shrink-0 items-center justify-center rounded-md bg-info/10 text-info [&_svg]:size-5">
						{icon}
					</div>
					<div className="min-w-0">
						<p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
							{eyebrow}
						</p>
						<h1 className="truncate font-serif text-2xl font-bold text-foreground">
							{title}
						</h1>
					</div>
				</div>
				{actions ? (
					<div className="flex shrink-0 items-center gap-2">{actions}</div>
				) : null}
			</header>

			{/* Metadata grid: responsive 2-or-3-column label/value pairs. */}
			<MetadataGrid metadata={metadata} />

			{/* Alert banner: left-accented, full-tint, optional trailing action. */}
			{alert && alertTone && AlertIcon ? (
				<div
					role="alert"
					className={cn(
						'flex items-center gap-3 rounded-lg border border-l-4 px-4 py-3 text-sm',
						alertTone.container,
					)}
				>
					<AlertIcon className="size-4 shrink-0" aria-hidden="true" />
					<div className="min-w-0 flex-1 text-foreground">{alert.message}</div>
					{alert.action ? (
						<div className="shrink-0">{alert.action}</div>
					) : null}
				</div>
			) : null}

			{/* Code/JSON panel: bordered, heading + corner copy button, mono pre. */}
			{codePanel ? (
				<CodePanel heading={codePanel.heading} content={codePanel.content} />
			) : null}

			{children}
		</PageContainer>
	);
}
